import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  collection,
  getDocs,
  updateDoc,
  doc,
  addDoc,
  serverTimestamp,
  query,
  where,
} from 'firebase/firestore';
import { firestore } from '../../database/firebase';
import {
  HiXMark,
  HiUserGroup,
  HiAcademicCap,
  HiArrowRight,
  HiCheckCircle,
  HiExclamationTriangle,
  HiShieldCheck,
} from 'react-icons/hi2';
import { toast } from 'react-hot-toast';

const TransferModal = ({ isOpen, onClose, client, currentUser, transferType }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedUser, setSelectedUser] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchUsers();
    }
  }, [isOpen, transferType]);

  const fetchUsers = async () => {
    setLoading(true);
    setError('');

    try {
      const collectionName = transferType === 'coach' ? 'coaches' : 'moderator';
      const usersRef = collection(firestore, collectionName);
      const q = query(usersRef, where('status', '==', 'active'));
      const snapshot = await getDocs(q);

      const usersData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));

      setUsers(usersData);
    } catch (err) {
      toast.error('Error fetching users: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTransfer = async () => {
    if (!selectedUser) {
      setError('Please select a user to transfer to');
      return;
    }

    const selectedUserData = users.find(user => user.id === selectedUser);
    if (!selectedUserData) {
      setError('Selected user not found');
      return;
    }

    if (transferType === 'coach') {
      if (client.assignedCoachEmail === selectedUserData.email) {
        setError('Client is already assigned to this coach');
        return;
      }
    } else if (transferType === 'moderator') {
      if (client.assignedEmail === selectedUserData.email) {
        setError('Client is already assigned to this moderator');
        return;
      }
    }

    setSubmitting(true);
    setError('');

    try {
      const clientRef = doc(firestore, 'clients', client.id);
      const updateData = {
        updatedAt: serverTimestamp(),
      };

      if (transferType === 'coach') {
        updateData.assignedCoachName =
          `${selectedUserData.firstname || ''} ${selectedUserData.lastname || ''}`.trim();
        updateData.assignedCoachEmail = selectedUserData.email;
      } else if (transferType === 'moderator') {
        updateData.assigned =
          `${selectedUserData.firstname || ''} ${selectedUserData.lastname || ''}`.trim();
        updateData.assignedEmail = selectedUserData.email;
      }

      await updateDoc(clientRef, updateData);

      const transferLogData = {
        transferredByEmail: currentUser.email,
        transferredByName:
          currentUser.displayName ||
          `${currentUser.firstname || ''} ${currentUser.lastname || ''}`.trim(),
        transferType: transferType,
        oldUser: transferType === 'coach' ? client.assignedCoachName : client.assigned,
        oldEmail: transferType === 'coach' ? client.assignedCoachEmail : client.assignedEmail,
        newUser: updateData[transferType === 'coach' ? 'assignedCoachName' : 'assigned'],
        newEmail: updateData[transferType === 'coach' ? 'assignedCoachEmail' : 'assignedEmail'],
        clientId: client.id,
        clientName: `${client.firstname || ''} ${client.lastname || ''}`.trim(),
        timestamp: serverTimestamp(),
      };

      const transferLogsRef = collection(firestore, 'clients', client.id, 'transferLogs');
      await addDoc(transferLogsRef, transferLogData);

      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        onClose();
      }, 2000);
    } catch (err) {
      toast.error('Error transferring user: ' + err.message);
      setError('Failed to transfer user. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      setSelectedUser('');
      setError('');
      setSuccess(false);
      onClose();
    }
  };

  const getCurrentAssignment = () => {
    if (transferType === 'coach') {
      return client.assignedCoachName || client.assignedCoachEmail || 'No coach assigned';
    } else {
      return client.assigned || client.assignedEmail || 'No moderator assigned';
    }
  };

  const getCurrentEmail = () => {
    if (transferType === 'coach') {
      return client.assignedCoachEmail || 'No email';
    } else {
      return client.assignedEmail || 'No email';
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
        >
          <motion.div
            initial={{ opacity: 0, y: 50, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 50, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="relative w-[92%] max-w-md rounded-xl border border-gray-200 bg-white shadow-2xl ring-1 ring-black/5 sm:w-full"
          >
            <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-blue-600">
                  {transferType === 'coach' ? (
                    <HiAcademicCap className="h-5 w-5 text-white" />
                  ) : (
                    <HiUserGroup className="h-5 w-5 text-white" />
                  )}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-black">
                    Transfer {transferType === 'coach' ? 'Coach' : 'Business Advisor'}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {client.firstname || ''} {client.lastname || ''}
                  </p>
                </div>
              </div>
              <button
                onClick={handleClose}
                disabled={submitting}
                className="rounded-full p-2 text-gray-400 transition-all duration-200 hover:bg-gray-100 hover:text-gray-600 disabled:cursor-not-allowed"
              >
                <HiXMark className="h-5 w-5" />
              </button>
            </div>

            <div className="p-4 md:p-6">
              {success ? (
                <div className="py-8 text-center">
                  <HiCheckCircle className="mx-auto h-12 w-12 text-green-500" />
                  <h3 className="mt-4 text-lg font-semibold text-black">Transfer Successful</h3>
                  <p className="mt-2 text-gray-600">
                    {transferType === 'coach' ? 'Coach' : 'Moderator'} has been transferred
                    successfully.
                  </p>
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="rounded-lg border border-gray-100 bg-gray-50/50 p-4">
                    <h4 className="mb-2 flex items-center gap-2 text-sm font-semibold text-gray-900">
                      <HiShieldCheck className="h-4 w-4 text-blue-600" />
                      Current Assignment
                    </h4>
                    <div className="space-y-2">
                      <div>
                        <p className="text-xs font-medium text-gray-500">Name</p>
                        <p className="text-sm font-semibold text-black">{getCurrentAssignment()}</p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-500">Email</p>
                        <p className="text-sm text-gray-700">{getCurrentEmail()}</p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">
                      Select New {transferType === 'coach' ? 'Coach' : 'Moderator'}
                    </label>
                    {loading ? (
                      <div className="flex items-center justify-center py-4">
                        <div className="inline-block h-6 w-6 animate-spin rounded-full border-4 border-blue-500 border-t-transparent"></div>
                        <span className="ml-2 text-sm text-gray-600">Loading users...</span>
                      </div>
                    ) : (
                      <select
                        value={selectedUser}
                        onChange={e => setSelectedUser(e.target.value)}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      >
                        <option value="">Select a user...</option>
                        {users.map(user => (
                          <option key={user.id} value={user.id}>
                            {user.firstname || ''} {user.lastname || ''} ({user.email})
                          </option>
                        ))}
                      </select>
                    )}
                    <p className="mt-1 text-xs text-gray-500">
                      Only active {transferType === 'coach' ? 'coaches' : 'business advisors'} are
                      shown.
                    </p>
                  </div>

                  {error && (
                    <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                      <div className="flex items-center gap-2">
                        <HiExclamationTriangle className="h-4 w-4 text-red-500" />
                        <span className="text-sm text-red-700">{error}</span>
                      </div>
                    </div>
                  )}

                  <div className="flex flex-col gap-2 border-t border-gray-100 pt-4 sm:flex-row sm:items-center sm:gap-3">
                    <button
                      type="button"
                      onClick={handleClose}
                      disabled={submitting}
                      className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed md:px-4"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleTransfer}
                      disabled={submitting || !selectedUser}
                      className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50 md:px-4"
                    >
                      {submitting ? (
                        <>
                          <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                          Transferring...
                        </>
                      ) : (
                        <>
                          <HiArrowRight className="h-4 w-4" />
                          Transfer {transferType === 'coach' ? 'Coach' : 'Moderator'}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default TransferModal;
